import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StarIcon from '@mui/icons-material/Star';
import NavigationCard from './NavigationCard';

/**
 * FavoritesSection Component
 * 
 * Displays a dedicated section for favorited navigation cards at the top of the home page.
 * The section is collapsible and respects the current view mode and card preferences.
 * 
 * @param {Object} props
 * @param {Array} props.favorites - Array of favorited navigation items
 * @param {function} props.onCardClick - Handler for card click
 * @param {function} props.onToggleFavorite - Handler for favorite toggle
 * @param {function} props.onTogglePin - Handler for pin toggle
 * @param {string} props.viewMode - Current view mode (grid/list/compact)
 * @param {number} props.cardSize - Card size preference
 * @param {number} props.cardSpacing - Card spacing preference
 * @param {Object} props.themeStyles - Theme-specific styling
 * @param {string} props.selectedCardId - ID of currently selected card
 * @param {Array} props.pinnedCards - Array of pinned card IDs
 * @param {string} props.searchQuery - Current search query for highlighting
 * @param {boolean} props.animationsEnabled - Whether animations are enabled
 */
const FavoritesSection = ({
  favorites = [],
  onCardClick,
  onToggleFavorite,
  onTogglePin,
  viewMode = 'grid',
  cardSize = 2,
  cardSpacing = 1.5,
  themeStyles = {},
  selectedCardId,
  pinnedCards = [],
  searchQuery = '',
  animationsEnabled = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Don't render if no favorites
  if (favorites.length === 0) {
    return null;
  }

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Calculate responsive grid layout based on view mode
  const getGridColumns = () => {
    if (viewMode === 'list') {
      return 1;
    }
    
    // Responsive columns based on viewport and view mode
    if (viewMode === 'grid') {
      return {
        xs: 2,  // Mobile: 2 columns
        sm: 3,  // Tablet: 3 columns
        md: selectedCardId ? 4 : 5,  // Desktop: 4-5 columns
        lg: selectedCardId ? 5 : 6,  // Large: 5-6 columns
        xl: selectedCardId ? 6 : 7,  // XL: 6-7 columns
      };
    } else if (viewMode === 'compact') {
      return {
        xs: 3,  // Mobile: 3 columns
        sm: 4,  // Tablet: 4 columns
        md: selectedCardId ? 6 : 7,  // Desktop: 6-7 columns
        lg: selectedCardId ? 7 : 9,  // Large: 7-9 columns
        xl: selectedCardId ? 8 : 10, // XL: 8-10 columns
      };
    }
    return 1;
  };

  const gridColumns = getGridColumns();
  const gap = cardSpacing * 8;
  const minCardWidth = viewMode === 'compact' ? '100px' : '120px';

  return (
    <Box sx={{ mb: 3 }}>
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          px: { xs: 0.5, sm: 1 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
          <StarIcon 
            sx={{ 
              color: 'white', 
              fontSize: { xs: 22, sm: 20 },
            }} 
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              // Mobile: Slightly larger text
              fontSize: { xs: '17px', sm: '16px' },
              fontFamily: '"brandon-grotesque-n7", "brandon-grotesque", sans-serif',
              ...themeStyles.pageTitle,
            }}
          >
            Favorites
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'white',
              fontSize: { xs: '13px', sm: '12px' },
            }}
          >
            ({favorites.length})
          </Typography>
        </Box>
        <IconButton
          onClick={handleToggleExpand}
          aria-label={isExpanded ? 'collapse favorites' : 'expand favorites'}
          size="small"
          sx={{
            color: 'white',
            transition: 'all 0.2s ease',
            // Mobile: Minimum touch target size
            minWidth: { xs: 44, sm: 'auto' },
            minHeight: { xs: 44, sm: 'auto' },
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          {isExpanded ? (
            <ExpandLessIcon 
              sx={{ 
                fontSize: { xs: 26, sm: 24 },
              }} 
            />
          ) : (
            <ExpandMoreIcon 
              sx={{ 
                fontSize: { xs: 26, sm: 24 },
              }} 
            />
          )}
        </IconButton>
      </Box>

      {/* Favorites Grid */}
      <Collapse in={isExpanded}>
        <Box
          sx={{
            display: viewMode === 'list' ? 'flex' : 'grid',
            flexDirection: viewMode === 'list' ? 'column' : undefined,
            // Responsive grid columns using breakpoints
            gridTemplateColumns: viewMode !== 'list' 
              ? {
                  xs: `repeat(${gridColumns.xs}, 1fr)`,
                  sm: `repeat(${gridColumns.sm}, 1fr)`,
                  md: `repeat(${gridColumns.md}, 1fr)`,
                  lg: `repeat(${gridColumns.lg}, 1fr)`,
                  xl: `repeat(${gridColumns.xl}, 1fr)`,
                }
              : undefined,
            // Responsive gap
            gap: { 
              xs: `${Math.max(gap * 0.5, 8)}px`,  // Smaller gap on mobile
              sm: `${Math.max(gap * 0.75, 12)}px`, // Medium gap on tablet
              md: `${gap}px`,  // Full gap on desktop
            },
            transition: 'all 0.3s ease',
          }}
        >
          {favorites.map((item) => (
            <Box
              key={item.id}
              sx={{
                minWidth: minCardWidth,
                width: viewMode === 'list' ? '100%' : 'auto',
                transition: 'all 0.3s ease',
              }}
            >
              <NavigationCard
                icon={item.icon}
                title={item.title}
                description={item.description}
                route={item.route}
                subItems={item.subItems}
                color={item.color}
                onClick={() => onCardClick(item)}
                isSelected={selectedCardId === item.id}
                themeStyles={themeStyles}
                viewMode={viewMode}
                cardSize={cardSize}
                searchQuery={searchQuery}
                isFavorite={true}
                isPinned={pinnedCards.includes(item.id)}
                onToggleFavorite={() => onToggleFavorite(item.id)}
                onTogglePin={() => onTogglePin(item.id)}
                animationsEnabled={animationsEnabled}
              />
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default FavoritesSection;
